import { Phone, Mail, Globe, User, Copy, Activity } from 'lucide-react'
import { motion } from 'framer-motion'
import { getTemperature } from '@/components/CloserLeads/Modals/closerLeadDetailsUtils'
import type { CloserLead } from '@/types'
import { negotiationLockNotice } from '@/components/CloserLeads/negotiationLockNotice'

interface CloserLeadDetailsInfoCardProps {
  lead: CloserLead
}

export function CloserLeadDetailsInfoCard({ lead }: CloserLeadDetailsInfoCardProps) {
  const temp = getTemperature(lead.score || 0)

  return (
    <motion.div key='details' initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <div className='flex flex-col sm:flex-row items-center sm:items-start gap-4 sm:gap-6'>
        <div className='w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-accent/10 border border-accent/20 flex items-center justify-center text-accent font-black text-2xl shrink-0'>
          {(lead.empresa || '?')[0].toUpperCase()}
        </div>
        <div className='flex-1 text-center sm:text-left'>
          <h3 className='text-xl sm:text-2xl font-black text-text-primary tracking-tight uppercase'>
            {lead.empresa}
          </h3>
          <div className='flex flex-wrap justify-center sm:justify-start items-center gap-2 mt-2'>
            <span
              className='flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wider border'
              style={{ color: temp.color, borderColor: `${temp.color}30`, background: `${temp.color}15` }}
            >
              <Activity size={12} /> {temp.label}
            </span>
            <span className='flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wider border border-white/[0.06] bg-white/[0.03] text-text-muted'>
              {lead.status}
            </span>
          </div>
        </div>
      </div>

      <div className='mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3'>
        <div className='glass-card p-4 flex items-center gap-3'>
          <div className='p-2 rounded-xl bg-accent/10 text-accent'><User size={16} /></div>
          <div className='min-w-0'>
            <span className='text-[9px] font-bold text-text-muted uppercase tracking-wider block'>Decisor</span>
            <span className='text-sm font-bold text-text-primary truncate block'>{lead.contato || '---'}</span>
            {lead.cargo && <span className='text-[10px] text-text-muted block'>{lead.cargo}</span>}
          </div>
        </div>
        <div className='glass-card p-4 flex items-center gap-3'>
          <div className='p-2 rounded-xl bg-accent/10 text-accent'><Phone size={16} /></div>
          <div className='min-w-0 flex-1'>
            <span className='text-[9px] font-bold text-text-muted uppercase tracking-wider block'>Telefone</span>
            {lead.exclusividade ? (
              <span className='text-sm font-bold text-amber-300'>{negotiationLockNotice(lead.exclusividade)}</span>
            ) : (
              <div className='flex items-center gap-2'>
                <span className='text-sm font-bold text-text-primary'>{lead.telefone || '---'}</span>
                <Copy size={12} className='text-text-muted cursor-pointer hover:text-accent shrink-0' />
              </div>
            )}
          </div>
        </div>
        <div className='glass-card p-4 flex items-center gap-3'>
          <div className='p-2 rounded-xl bg-accent/10 text-accent'><Mail size={16} /></div>
          <div className='min-w-0'>
            <span className='text-[9px] font-bold text-text-muted uppercase tracking-wider block'>Email</span>
            <span className='text-sm font-bold text-text-primary truncate block'>{lead.email || '---'}</span>
          </div>
        </div>
        <div className='glass-card p-4 flex items-center gap-3'>
          <div className='p-2 rounded-xl bg-accent/10 text-accent'><Globe size={16} /></div>
          <div className='min-w-0'>
            <span className='text-[9px] font-bold text-text-muted uppercase tracking-wider block'>Site</span>
            <span className='text-sm font-bold text-text-primary truncate block'>{lead.site || '---'}</span>
          </div>
        </div>
      </div>

      <div className='mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3'>
        {lead.segmento && (
          <div className='text-center'>
            <span className='text-[9px] font-bold text-text-muted uppercase tracking-wider block'>Segmento</span>
            <span className='text-[11px] font-semibold text-text-primary'>{lead.segmento}</span>
          </div>
        )}
        {lead.plataforma && (
          <div className='text-center'>
            <span className='text-[9px] font-bold text-text-muted uppercase tracking-wider block'>Plataforma</span>
            <span className='text-[11px] font-semibold text-text-primary'>{lead.plataforma}</span>
          </div>
        )}
        {lead.modelo && (
          <div className='text-center'>
            <span className='text-[9px] font-bold text-text-muted uppercase tracking-wider block'>Modelo</span>
            <span className='text-[11px] font-semibold text-text-primary'>{lead.modelo}</span>
          </div>
        )}
        {lead.faturamento && (
          <div className='text-center'>
            <span className='text-[9px] font-bold text-text-muted uppercase tracking-wider block'>Faturamento</span>
            <span className='text-[11px] font-semibold text-text-primary'>{lead.faturamento}</span>
          </div>
        )}
      </div>

      {lead.observacoes && (
        <div className='mt-4 p-4 bg-white/[0.02] rounded-xl border border-white/[0.06]'>
          <span className='text-[9px] font-bold text-text-muted uppercase tracking-wider block mb-2'>Observações</span>
          <p className='text-[11px] text-text-primary font-medium leading-relaxed'>{lead.observacoes}</p>
        </div>
      )}

      <div className='mt-4 p-4 bg-white/[0.02] rounded-xl border border-white/[0.06]'>
        <div className='flex items-center justify-between mb-2'>
          <span className='text-[9px] font-bold text-text-muted uppercase tracking-wider'>Fluxo de Cadência</span>
          <span className='text-[9px] font-bold text-accent'>Step {lead.tentativas || 0} de 15</span>
        </div>
        <div className='flex gap-0.5 h-2'>
          {[...Array(15)].map((_, i) => (
            <div
              key={i}
              className='flex-1 rounded-sm transition-all duration-500'
              style={{
                backgroundColor: i < (lead.tentativas || 0) ? '#14bdbd' : 'rgba(255,255,255,0.05)',
                boxShadow: i < (lead.tentativas || 0) ? '0 0 8px #14bdbd40' : 'none',
              }}
            />
          ))}
        </div>
      </div>
    </motion.div>
  )
}
